// ReviewsTab.jsx — the "Reviews" page in the sidebar.
//   Responses   dashboard (ratings, recommendations, public-use consent, trends, written
//               feedback) and every review with its answers
//   Questions   edit the question set per trip type — no developer needed
//   Emails      the review email and reminder wording, buttons, branding and social links
//   Automation  switch automatic emails on/off, reminder delay, link address, tests, dry run
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { usePaginated } from "../hooks";
import { useToast, useConfirm, Btn, Card, Inp, Modal, Spinner, ApiError, Pages } from "../ui";
import { useAuth } from "../auth";
import { BRAND } from "../brand";
import { BRAND_HEX } from "../theme";
import {
  ReviewQuestions, ReviewStatusPill, PublicUseBadge, ConsentPanel, LockedDetails, Stars, QUESTION_TYPES,
  errMsg, fmtDay, fmtDateTime, dateRange,
} from "./reviewShared";
import { StepForm } from "./PublicReviewPage";

const VIEWS = [
  { id: "responses", label: "Responses" },
  { id: "questions", label: "Questions" },
  { id: "emails", label: "Emails" },
  { id: "automation", label: "Automation" },
];

// The company name in emails and questions ({brand}) comes from BRAND_NAME in the backend
// environment (brand.config.ts on the server) — the server's version of brand.js.
const STAFF_EMAIL_EXAMPLE = `you@${BRAND.emailDomain || "example.com"}`;
const CRM_URL_EXAMPLE = `https://crm.${BRAND.emailDomain || "example.com"}`;

export function ReviewsTab() {
  const [view, setView] = useState("responses");

  // First visit: if the server has no BRAND_COLOR and none was saved, use the app's theme colour.
  useEffect(() => {
    api.get("/crm/reviews/settings").then((s) => {
      if (s.brandColor || s.brand?.color) return;
      api.patch("/crm/reviews/settings", { brandColor: BRAND_HEX }).catch(() => {});
    }).catch(() => {});
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Reviews</h1>
          <p className="text-sm text-slate-400">Client feedback after every trip</p>
        </div>
        <div className="flex gap-1 p-1 rounded-xl bg-slate-100 overflow-x-auto">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${view === v.id ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      {view === "responses" && <ResponsesView />}
      {view === "questions" && <QuestionsView />}
      {view === "emails" && <EmailsView />}
      {view === "automation" && <AutomationView />}
    </div>
  );
}

export default ReviewsTab;

// ════════════════════════════════════════════════════════════════════════════
// RESPONSES
// ════════════════════════════════════════════════════════════════════════════
const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "submitted", label: "Submitted" },
  { value: "waiting", label: "Waiting" },
  { value: "opened", label: "Opened, not submitted" },
  { value: "failed", label: "Email failed" },
  { value: "pending", label: "Not sent" },
];
const PUBLIC_FILTERS = [
  { value: "all", label: "Any" },
  { value: "ok", label: "OK to publish" },
  { value: "approval", label: "Ask client first" },
  { value: "private", label: "Private" },
];

function ResponsesView() {
  const [status, setStatus] = useState("all");
  const [formType, setFormType] = useState("all");
  const [publicUse, setPublicUse] = useState("all");
  const [tests, setTests] = useState(false);
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [stats, setStats] = useState(null);
  const [forms, setForms] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(typed.trim()), 350);
    return () => clearTimeout(t);
  }, [typed]);

  const loadStats = useCallback(() => {
    api.get("/crm/reviews/stats").then(setStats).catch(() => setStats(null));
  }, []);
  useEffect(() => {
    loadStats();
    api.get("/crm/reviews/forms").then((r) => setForms(r.forms || [])).catch(() => {});
  }, [loadStats]);

  const qs = new URLSearchParams({
    status, formType, publicUse, tests: tests ? "only" : "exclude", ...(search ? { search } : {}),
  }).toString();
  const { data, page, totalPages, total, loading, error, setPage, refetch } = usePaginated(`/crm/reviews?${qs}`, 20);

  const showPublic = (v) => {
    setPublicUse(v);
    setStatus("submitted");
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex flex-col gap-5">
      <Dashboard stats={stats} onOpen={setOpenId} onPublicFilter={showPublic} />

      {/* Filters */}
      <div ref={listRef} />
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <Inp label="Search" placeholder="Client, email or destination" value={typed} onChange={(e) => setTyped(e.target.value)} />
        </div>
        <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_FILTERS} />
        <FilterSelect label="Trip type" value={formType} onChange={setFormType}
          options={[{ value: "all", label: "All types" }, ...forms.map((f) => ({ value: f.tripType, label: f.name || f.tripType }))]} />
        <FilterSelect label="Public use" value={publicUse} onChange={setPublicUse} options={PUBLIC_FILTERS} />
        <label className="flex items-center gap-2 text-sm text-slate-600 pb-2.5 cursor-pointer select-none">
          <input type="checkbox" checked={tests} onChange={(e) => setTests(e.target.checked)} />
          Test reviews only
        </label>
      </Card>

      {/* List */}
      <Card className="overflow-hidden">
        {error ? (
          <ApiError msg={errMsg({ message: error })} onRetry={refetch} />
        ) : loading && !data.length ? (
          <Spinner />
        ) : !data.length ? (
          <div className="py-14 text-center">
            <p className="font-semibold text-slate-700 mb-1">No reviews here yet</p>
            <p className="text-sm text-slate-400 max-w-sm mx-auto">
              {tests ? "Send a test from the Emails or Automation tab to see one." : "Reviews appear once a trip is completed and its review email has gone out."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.map((r) => (
              <button key={r._id} onClick={() => setOpenId(r._id)}
                className="w-full text-left px-4 sm:px-5 py-3.5 hover:bg-slate-50 transition-colors grid grid-cols-2 lg:grid-cols-[1.1fr_1.3fr_6.5rem_9.5rem_auto] items-center gap-x-4 gap-y-1.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {r.clientName || "—"}{r.isTest && <span className="ml-2 text-[10px] font-bold text-amber-600 uppercase">Test</span>}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{r.email}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-700 truncate">{r.destination || "—"}</p>
                  <p className="text-xs text-slate-400 truncate">{r.tripTypeLabel}{r.endDate ? ` · ${dateRange(r.startDate, r.endDate)}` : ""}</p>
                </div>
                <div>{r.overallRating ? <Stars value={r.overallRating} size={14} /> : <span className="text-xs text-slate-300">—</span>}</div>
                <div className="text-xs text-slate-400">
                  {r.submittedAt ? `Answered ${fmtDay(r.submittedAt)}` : r.firstSentAt ? `Sent ${fmtDay(r.firstSentAt)}${r.reminderSentAt ? " · reminded" : ""}` : "Not sent"}
                </div>
                <div className="flex flex-wrap gap-1.5 lg:justify-end col-span-2 lg:col-span-1">
                  <ReviewStatusPill review={r} />
                  <PublicUseBadge review={r} />
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
      {data.length > 0 && <Pages page={page} totalPages={totalPages} total={total} onPage={setPage} loading={loading} />}

      {openId && (
        <ReviewDetailModal id={openId} onClose={() => setOpenId(null)}
          onChanged={() => { refetch(); loadStats(); }}
          onDeleted={() => { setOpenId(null); refetch(); loadStats(); }} />
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ stats: s, onOpen, onPublicFilter }) {
  if (!s) return <div className="h-28 rounded-2xl bg-white border border-slate-100 animate-pulse" />;
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  const maxRec = Math.max(1, ...s.recommend.map((r) => r.count));
  const recTotal = s.recommend.reduce((a, r) => a + r.count, 0);
  const maxMonth = Math.max(1, ...s.byMonth.map((m) => m.submitted));

  return (
    <div className="flex flex-col gap-4">
      {/* Headline numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Response rate" value={s.responseRate != null ? `${s.responseRate}%` : "—"}
          sub={`${s.submitted} of ${s.sent + s.submitted} answered`} />
        <Stat label="Average rating"
          value={s.avgRating != null ? <span className="flex items-center gap-2">{s.avgRating}<Stars value={Math.floor(s.avgRating + 0.25)} size={15} /></span> : "—"}
          sub={`overall rating · ${plural(s.ratedCount, "review")}`} />
        <Stat label="Would recommend" value={s.recommendPct != null ? `${s.recommendPct}%` : "—"}
          sub={`"Definitely" or "Probably" · ${plural(s.recommendCount, "answer")}`} />
        <Stat label="Waiting for answers" value={s.sent}
          sub={`${s.opened} opened${s.failed ? ` · ${s.failed} email failed` : ""}`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recommendation answers — one bar per answer */}
        <Card className="p-5">
          <CardTitle title="How likely are you to recommend us?" sub={recTotal ? `${plural(recTotal, "answer")}` : "No answers yet"} />
          <div className="flex flex-col gap-2.5 mt-4">
            {s.recommend.length ? s.recommend.map((r) => (
              <div key={r.answer} className="grid grid-cols-[7.5rem_1fr_3.5rem] items-center gap-3 text-sm" title={`${r.answer}: ${r.count} (${Math.round((r.count / recTotal) * 100)}%)`}>
                <span className="text-slate-600 truncate">{r.answer}</span>
                <div className="h-3 rounded-r bg-slate-100">
                  <div className="h-3 rounded-r" style={{ width: `${(r.count / maxRec) * 100}%`, background: BRAND_HEX }} />
                </div>
                <span className="text-right text-slate-500 tabular-nums">{r.count} · {Math.round((r.count / recTotal) * 100)}%</span>
              </div>
            )) : <p className="text-sm text-slate-400">Answers appear here once clients submit.</p>}
          </div>
        </Card>

        {/* Public-use consent */}
        <Card className="p-5">
          <CardTitle title="Can we share their feedback?" sub="From the permission questions at the end of each form" />
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              ["ok", "OK to publish", s.consent.ok, "Includes clients who approved after being asked"],
              ["approval", "Ask client first", s.consent.approval, "They asked to approve before anything is published"],
              ["private", "Keep private", s.consent.private, "Never publish these"],
            ].map(([key, label, n, hint]) => (
              <button key={key} type="button" onClick={() => onPublicFilter(key)} title={hint}
                className="text-left p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
                <p className="text-2xl font-bold text-slate-800">{n}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Consent covers written feedback only — photos or videos need separate permission.</p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 overflow-x-auto">
          <CardTitle title="By trip type" />
          <BreakdownTable rows={s.byType.map((t) => ({ key: t.formType, name: t.name, ...t }))} firstCol="Type" />
        </Card>
        <Card className="p-5 overflow-x-auto">
          <CardTitle title="By destination" sub="Destinations with at least one answer" />
          {s.byDestination.length
            ? <BreakdownTable rows={s.byDestination.map((d) => ({ key: d.destination, name: d.destination, ...d }))} firstCol="Destination" />
            : <p className="text-sm text-slate-400 mt-3">No answers yet.</p>}
        </Card>
      </div>

      {/* Last 6 months — answers per month, average rating written under each bar */}
      <Card className="p-5">
        <CardTitle title="Answers per month" sub="Last 6 months · average rating under each month" />
        <div className="grid grid-cols-6 gap-3 items-end h-40 mt-4">
          {s.byMonth.map((m) => (
            <div key={m.month} className="flex flex-col items-center justify-end h-full gap-1.5" title={`${m.label}: ${m.submitted} answered${m.avgRating != null ? `, average ${m.avgRating}★` : ""}`}>
              <span className="text-xs text-slate-500 tabular-nums">{m.submitted || ""}</span>
              <div className="w-full max-w-[2.5rem] rounded-t" style={{ height: `${Math.max(m.submitted ? 6 : 2, (m.submitted / maxMonth) * 100)}px`, background: m.submitted ? BRAND_HEX : "#E2E8F0" }} />
              <span className="text-xs text-slate-600">{m.label}</span>
              <span className="text-[11px] text-slate-400 h-4">{m.avgRating != null ? `${m.avgRating}★` : ""}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Written feedback */}
      <Card className="p-5">
        <CardTitle title="Latest written feedback" sub="Newest first · click to see the full review" />
        {s.feedback.length ? (
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            {s.feedback.slice(0, 8).map((f, i) => (
              <button key={i} type="button" onClick={() => onOpen(f.reviewId)}
                className="text-left p-4 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors flex flex-col gap-2">
                <p className="text-[11px] text-slate-400">{f.question}</p>
                <p className="text-sm text-slate-700 leading-relaxed line-clamp-4">“{f.text}”</p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-auto">
                  <span className="font-semibold text-slate-700">{f.clientName}</span>
                  <span>· {f.destination || f.tripTypeLabel}</span>
                  <span>· {fmtDay(f.submittedAt)}</span>
                  {f.overallRating ? <Stars value={f.overallRating} size={12} /> : null}
                  <PublicUseBadge review={{ status: "submitted", consent: f.publicUse === "ok" ? "publish" : f.publicUse === "approval" ? "approval" : f.publicUse === "private" ? "private" : null }} />
                </div>
              </button>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 mt-3">Written answers appear here once clients submit.</p>}
      </Card>
    </div>
  );
}

function CardTitle({ title, sub }) {
  return (
    <div>
      <p className="text-sm font-bold text-slate-800">{title}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function BreakdownTable({ rows, firstCol }) {
  return (
    <table className="w-full text-sm mt-3">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
          <th className="py-2 font-semibold">{firstCol}</th>
          <th className="py-2 font-semibold text-right">Answered</th>
          <th className="py-2 font-semibold text-right">Avg rating</th>
          <th className="py-2 font-semibold text-right">Recommend</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r) => (
          <tr key={r.key}>
            <td className="py-2 text-slate-700 pr-3">{r.name}</td>
            <td className="py-2 text-right text-slate-600 tabular-nums">{r.submitted}/{r.total}</td>
            <td className="py-2 text-right text-slate-600 tabular-nums">{r.avgRating != null ? `${r.avgRating}★` : "—"}</td>
            <td className="py-2 text-right text-slate-600 tabular-nums">{r.recommendPct != null ? `${r.recommendPct}%` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Stat({ label, value, sub, hint }) {
  return (
    <Card className="p-4" title={hint}>
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{label}</p>
      <div className="text-2xl font-bold text-slate-800 leading-none mb-1">{value}</div>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ReviewDetailModal({ id, onClose, onChanged, onDeleted }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth() || {};
  const [state, setState] = useState({ loading: true, error: null, r: null });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/crm/reviews/${id}`)
      .then((r) => setState({ loading: false, error: null, r }))
      .catch((e) => setState({ loading: false, error: errMsg(e), r: null }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const r = state.r;
  const canDelete = r && (r.isTest || r.status !== "submitted");

  const remove = async () => {
    const ok = await confirm({
      title: r.isTest ? "Delete this test review?" : "Delete this unanswered review?",
      message: r.isTest ? "It is only a test — nothing is sent." : "The client's link will stop working.",
      confirmText: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/crm/reviews/${id}`);
      toast("Review deleted");
      onDeleted();
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  const recordApproval = async (note) => {
    setBusy(true);
    try {
      const fresh = await api.post(`/crm/reviews/${id}/approval`, { by: user || "", note });
      setState({ loading: false, error: null, r: fresh });
      toast("Approval recorded ✓");
      onChanged?.();
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(r.link); toast("Link copied ✓"); }
    catch { window.prompt("Copy the review link:", r.link); }
  };

  return (
    <Modal title={r ? `${r.clientName || "Review"}${r.isTest ? " (test)" : ""}` : "Review"} onClose={onClose} wide>
      {state.loading ? <Spinner /> : state.error ? <ApiError msg={state.error} /> : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <ReviewStatusPill review={r} />
            <span className="text-xs text-slate-400">
              {r.firstSentAt ? `Sent ${fmtDateTime(r.firstSentAt)}` : "Not sent"}
              {r.reminderSentAt ? ` · reminder ${fmtDateTime(r.reminderSentAt)}` : ""}
              {r.submittedAt ? ` · answered ${fmtDateTime(r.submittedAt)}` : ""}
            </span>
            <div className="ml-auto flex gap-2">
              {r.link && r.status !== "submitted" && <Btn size="sm" variant="secondary" onClick={copy}>Copy link</Btn>}
              {canDelete && <Btn size="sm" variant="danger" onClick={remove}>Delete</Btn>}
            </div>
          </div>
          {r.lastError && r.status !== "submitted" && (
            <div className="px-3.5 py-2.5 rounded-xl bg-rose-50 border border-rose-100 text-xs text-rose-600">Last email failed: {r.lastError}</div>
          )}
          <ConsentPanel review={r} onRecordApproval={recordApproval} busy={busy} />
          <LockedDetails locked={{
            name: r.clientName, email: r.email, destination: r.destination, serviceType: r.tripTypeLabel,
            services: r.services, startDate: r.startDate, endDate: r.endDate,
          }} />
          {r.questions?.length ? (
            <div>
              <p className="text-[11px] text-slate-400 mb-4">
                {r.status === "submitted" ? "Answers, exactly as asked" : "Questions the client sees"}
                {r.formVersion ? ` · question set v${r.formVersion}` : ""}
              </p>
              <ReviewQuestions questions={r.questions} />
            </div>
          ) : (
            <p className="text-sm text-slate-400">The client hasn't opened the form yet.</p>
          )}
          {r.status === "submitted" && !r.isTest && (
            <p className="text-[11px] text-slate-400">Submitted feedback is kept as it was given and can't be edited or deleted. It also shows on the client's trip under the Review tab.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// QUESTIONS
// ════════════════════════════════════════════════════════════════════════════
const EDITABLE_KEYS = ["name", "title", "intro", "thankYouMessage"];
const newId = () => `q_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const hasFixedAnswers = (q) => q.type === "choice" || q.type === "multi_choice" || q.type === "yes_no";
const fixedAnswers = (q) => (q.type === "yes_no" ? ["yes", "no"] : (q.options || []).map((o) => o.trim()).filter(Boolean));

/** What these question ids are used for (they keep working whatever the wording). */
const SPECIAL_NOTES = {
  overall: "Used for the average rating on the dashboard.",
  recommend: "Used for “Would recommend” on the dashboard — keep the best answer first.",
  consent_public: "Public-use permission. Keep this order: 1st = may publish, 2nd = ask the client first, 3rd = keep private.",
  consent_identify: "Saved as how to name the client if their feedback is shared.",
  consent_contact: "Saved as whether we may contact them about their feedback.",
};

/** Only the fields the API accepts (extra fields would be rejected by strict validation). */
function toPayload(d) {
  const out = {};
  EDITABLE_KEYS.forEach((k) => { if (d[k] !== undefined) out[k] = d[k] ?? ""; });
  out.questions = (d.questions || []).map((q) => {
    const x = { id: q.id, label: q.label || "", type: q.type, required: !!q.required };
    if (q.helpText) x.helpText = q.helpText;
    if (q.type === "choice" || q.type === "multi_choice") x.options = (q.options || []).map((o) => o.trim()).filter(Boolean);
    if (q.showIf?.questionId) x.showIf = { questionId: q.showIf.questionId, anyOf: q.showIf.anyOf || [] };
    return x;
  });
  return out;
}

function QuestionsView() {
  const toast = useToast();
  const confirm = useConfirm();
  const [forms, setForms] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [type, setType] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/crm/reviews/forms");
      setForms(r.forms || []);
      setPlaceholders(r.placeholders || []);
      setError(null);
      return r.forms || [];
    } catch (e) {
      setError(errMsg(e));
      return [];
    }
  }, []);

  useEffect(() => {
    load().then((list) => {
      if (list.length) {
        setType(list[0].tripType);
        setDraft(JSON.parse(JSON.stringify(list[0])));
      }
    });
  }, [load]);

  const saved = useMemo(() => forms?.find((f) => f.tripType === type), [forms, type]);
  const dirty = useMemo(
    () => !!(saved && draft && JSON.stringify(toPayload(saved)) !== JSON.stringify(toPayload(draft))),
    [saved, draft],
  );

  const pick = async (t) => {
    if (t === type) return;
    if (dirty && !(await confirm({ title: "Discard unsaved changes?", message: "Your edits to this question set haven't been saved.", confirmText: "Discard" }))) return;
    const f = forms.find((x) => x.tripType === t);
    setType(t);
    setDraft(JSON.parse(JSON.stringify(f)));
  };

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const setQ = (i, patch) => setDraft((d) => ({ ...d, questions: d.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) }));
  const moveQ = (i, dir) => setDraft((d) => {
    const qs = [...d.questions];
    const j = i + dir;
    if (j < 0 || j >= qs.length) return d;
    [qs[i], qs[j]] = [qs[j], qs[i]];
    return { ...d, questions: qs };
  });
  const removeQ = async (i) => {
    const q = draft.questions[i];
    const dependents = draft.questions.filter((x) => x.showIf?.questionId === q.id);
    if (SPECIAL_NOTES[q.id] || dependents.length) {
      const ok = await confirm({
        title: "Remove this question?",
        message: [SPECIAL_NOTES[q.id], dependents.length ? `${dependents.length} question(s) only show based on this answer — they'll always show instead.` : ""].filter(Boolean).join(" "),
        confirmText: "Remove",
      });
      if (!ok) return;
    }
    setDraft((d) => ({
      ...d,
      questions: d.questions.filter((_, j) => j !== i).map((x) => (x.showIf?.questionId === q.id ? { ...x, showIf: undefined } : x)),
    }));
  };
  const addQ = () => setDraft((d) => ({ ...d, questions: [...d.questions, { id: newId(), label: "", type: "rating", required: false }] }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.patch(`/crm/reviews/forms/${type}`, toPayload(draft));
      setForms((list) => list.map((f) => (f.tripType === type ? updated : f)));
      setDraft(JSON.parse(JSON.stringify(updated)));
      toast(`Saved ✓ (version ${updated.version})`);
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const ok = await confirm({
      title: "Restore the default questions?",
      message: "This replaces the questions for this trip type with the team's original form. Reviews already opened keep theirs.",
      confirmText: "Restore",
    });
    if (!ok) return;
    try {
      const updated = await api.post(`/crm/reviews/forms/${type}/reset`, {});
      setForms((list) => list.map((f) => (f.tripType === type ? updated : f)));
      setDraft(JSON.parse(JSON.stringify(updated)));
      toast("Defaults restored ✓");
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  const openPreview = async () => {
    try {
      setPreview(await api.post(`/crm/reviews/forms/${type}/preview`, toPayload(draft)));
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  if (error) return <ApiError msg={error} onRetry={load} />;
  if (!forms || !draft) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {forms.map((f) => (
          <button key={f.tripType} onClick={() => pick(f.tripType)}
            className="px-4 py-2 rounded-xl text-sm font-semibold border transition-all"
            style={f.tripType === type ? { background: BRAND_HEX, borderColor: BRAND_HEX, color: "#fff" } : { background: "#fff", borderColor: "#E2E8F0", color: "#475569" }}>
            {f.name || f.tripType}
          </button>
        ))}
      </div>

      <div className="px-4 py-3 rounded-xl bg-slate-100 text-xs text-slate-500 leading-relaxed">
        {type === "other" ? "Used for every trip type that has no question set of its own. " : ""}
        Clients see one question per page. Saved changes apply to review forms opened from now on — reviews a client has
        already opened or answered keep the questions they saw. Placeholders: {placeholders.map((p) => <code key={p} className="mx-0.5 px-1 rounded bg-white text-slate-600">{p}</code>)}
      </div>

      {/* Form copy */}
      <Card className="p-5 flex flex-col gap-4">
        <h3 className="text-sm font-bold text-slate-800">First page and thank-you page</h3>
        <Inp label="Heading" value={draft.title || ""} onChange={set("title")} />
        <Area label="Intro" rows={2} value={draft.intro || ""} onChange={set("intro")} />
        <Area label="Thank-you message (after submitting)" rows={2} value={draft.thankYouMessage || ""} onChange={set("thankYouMessage")} />
      </Card>

      {/* Questions */}
      <Card className="p-5 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Questions ({draft.questions.length})</h3>
          <span className="text-[11px] text-slate-400">Version {saved?.version || 1}</span>
        </div>
        {draft.questions.map((q, i) => (
          <QuestionEditor key={q.id} q={q} index={i} count={draft.questions.length}
            earlier={draft.questions.slice(0, i).filter(hasFixedAnswers)}
            onChange={(patch) => setQ(i, patch)} onMove={(dir) => moveQ(i, dir)} onRemove={() => removeQ(i)} />
        ))}
        <div><Btn size="sm" variant="secondary" onClick={addQ}>+ Add question</Btn></div>
      </Card>

      {/* Actions */}
      <div className="sticky bottom-0 -mx-1 px-1 py-3 bg-slate-50/95 backdrop-blur flex flex-wrap items-center gap-2 border-t border-slate-200">
        <Btn onClick={save} loading={saving} disabled={!dirty}>Save {saved?.name || ""} questions</Btn>
        <Btn variant="secondary" onClick={openPreview}>Preview</Btn>
        {dirty && (
          <Btn variant="ghost" onClick={() => setDraft(JSON.parse(JSON.stringify(saved)))}>Undo changes</Btn>
        )}
        <span className="flex-1" />
        <Btn variant="ghost" onClick={reset}>Restore defaults</Btn>
        {dirty && <span className="w-full text-xs text-amber-600">Unsaved changes</span>}
      </div>

      {preview && (
        <Modal title="Preview — what the client sees (sample trip)" onClose={() => setPreview(null)} wide>
          <div className="max-w-xl mx-auto"><StepForm data={preview} preview /></div>
        </Modal>
      )}
    </div>
  );
}

function QuestionEditor({ q, index, count, earlier, onChange, onMove, onRemove }) {
  const hasOptions = q.type === "choice" || q.type === "multi_choice";
  const parent = q.showIf ? earlier.find((e) => e.id === q.showIf.questionId) : null;
  const parentAnswers = parent ? fixedAnswers(parent) : [];
  const anyOf = q.showIf?.anyOf || [];
  const stale = anyOf.filter((a) => !parentAnswers.includes(a));

  const toggleCondition = (on) => {
    if (!on) return onChange({ showIf: undefined });
    const first = earlier[earlier.length - 1];
    onChange({ showIf: first ? { questionId: first.id, anyOf: [] } : undefined });
  };

  return (
    <div className="rounded-xl border border-slate-200 p-3.5 flex flex-col gap-2.5">
      <div className="flex flex-wrap items-start gap-2">
        <span className="w-6 pt-2.5 text-xs font-bold text-slate-400">{index + 1}.</span>
        <input value={q.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Question wording"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400" />
        <select value={q.type} onChange={(e) => onChange({ type: e.target.value })}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none">
          {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="pl-8 flex flex-col gap-2">
        {SPECIAL_NOTES[q.id] && <p className="text-[11px] text-sky-700 bg-sky-50 rounded-lg px-2.5 py-1.5">{SPECIAL_NOTES[q.id]}</p>}
        <input value={q.helpText || ""} onChange={(e) => onChange({ helpText: e.target.value })} placeholder="Help text (optional)"
          className="px-3 py-1.5 rounded-lg border border-slate-100 text-xs text-slate-600 outline-none focus:border-slate-300" />
        {hasOptions && (
          <textarea rows={Math.max(3, (q.options || []).length + 1)} value={(q.options || []).join("\n")}
            onChange={(e) => onChange({ options: e.target.value.split("\n") })}
            placeholder={"One option per line\nDefinitely\nProbably\nNot sure"}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 resize-y" />
        )}

        {/* Show only if … */}
        {earlier.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none w-fit">
              <input type="checkbox" checked={!!q.showIf} onChange={(e) => toggleCondition(e.target.checked)} />
              Only show this question depending on an earlier answer
            </label>
            {q.showIf && (
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                  <span>Show when the answer to</span>
                  <select value={q.showIf.questionId} onChange={(e) => onChange({ showIf: { questionId: e.target.value, anyOf: [] } })}
                    className="max-w-full sm:max-w-sm px-2 py-1 rounded-md border border-slate-200 bg-white text-xs outline-none">
                    {earlier.map((e) => <option key={e.id} value={e.id}>{(e.label || "Untitled question").slice(0, 70)}</option>)}
                  </select>
                  <span>is any of:</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {parentAnswers.map((a) => (
                    <label key={a} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
                      <input type="checkbox" checked={anyOf.includes(a)}
                        onChange={(e) => onChange({ showIf: { ...q.showIf, anyOf: e.target.checked ? [...anyOf, a] : anyOf.filter((x) => x !== a) } })} />
                      {parent.type === "yes_no" ? (a === "yes" ? "Yes" : "No") : a}
                    </label>
                  ))}
                </div>
                {stale.length > 0 && <p className="text-[11px] text-rose-500">The earlier question's options changed — re-tick the answers above.</p>}
                {!anyOf.length && <p className="text-[11px] text-amber-600">Tick at least one answer.</p>}
                <p className="text-[11px] text-slate-400">When hidden, the question is skipped and never required.</p>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5 text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={!!q.required} onChange={(e) => onChange({ required: e.target.checked })} />
            Required{q.showIf ? " (when shown)" : ""}
          </label>
          <span className="flex-1" />
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30">↑ Up</button>
          <button type="button" disabled={index === count - 1} onClick={() => onMove(1)} className="px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30">↓ Down</button>
          <button type="button" onClick={onRemove} className="px-2 py-1 rounded-lg hover:bg-rose-50 text-rose-500">Remove</button>
        </div>
      </div>
    </div>
  );
}

function Area({ label, rows = 3, hint, ...p }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
      <textarea {...p} rows={rows}
        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:border-slate-400 resize-y" />
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EMAILS
// ════════════════════════════════════════════════════════════════════════════
const EMAIL_FIELDS = ["emailSubject", "emailBody", "reminderSubject", "reminderBody"];
const BRAND_FIELDS = ["brandColor", "logoUrl", "heroImageUrl"];

function EmailsView() {
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth() || {};
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [kind, setKind] = useState("initial");
  const [preview, setPreview] = useState(null);
  const [testing, setTesting] = useState(false);

  const apply = (data) => {
    setS(data);
    const f = { socials: { ...(data.socials || {}) } };
    BRAND_FIELDS.forEach((k) => { f[k] = data[k] || ""; });
    EMAIL_FIELDS.forEach((k) => { f[k] = data.effectiveEmail?.[k] || ""; });
    setForm(f);
  };

  const load = useCallback(async () => {
    try { apply(await api.get("/crm/reviews/settings")); setError(null); }
    catch (e) { setError(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const payload = useMemo(() => {
    if (!form) return null;
    const p = { socials: {} };
    BRAND_FIELDS.forEach((k) => { p[k] = String(form[k] || "").trim(); });
    EMAIL_FIELDS.forEach((k) => { p[k] = form[k] || ""; });
    (s?.socialKeys || []).forEach(({ key }) => { p.socials[key] = String(form.socials?.[key] || "").trim(); });
    return p;
  }, [form, s]);

  const savedPayload = useMemo(() => {
    if (!s) return null;
    const p = { socials: {} };
    BRAND_FIELDS.forEach((k) => { p[k] = s[k] || ""; });
    EMAIL_FIELDS.forEach((k) => { p[k] = s.effectiveEmail?.[k] || ""; });
    (s.socialKeys || []).forEach(({ key }) => { p.socials[key] = s.socials?.[key] || ""; });
    return p;
  }, [s]);
  const dirty = payload && savedPayload && JSON.stringify(payload) !== JSON.stringify(savedPayload);

  // Live preview of the email with the unsaved edits.
  useEffect(() => {
    if (!payload) return;
    const t = setTimeout(() => {
      api.post(`/crm/reviews/email/preview?kind=${kind}`, payload).then(setPreview).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [payload, kind]);

  const save = async () => {
    setSaving(true);
    try {
      apply(await api.patch("/crm/reviews/settings", payload));
      toast("Emails saved ✓");
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const restoreWording = async () => {
    const ok = await confirm({ title: "Restore the default wording?", message: "The subject and text of both emails go back to the original. Save to keep it.", confirmText: "Restore", danger: false });
    if (!ok) return;
    setForm((f) => ({ ...f, ...s.defaultEmail }));
  };

  const sendTest = async () => {
    if (dirty) return toast("Save your changes first — the test uses the saved emails", "warn");
    if (!user) return toast("Sign in again to send yourself a test", "error");
    setTesting(true);
    try {
      const res = await api.post("/crm/reviews/test-send", { to: user, formTypes: ["grouptrip"], kind });
      const r = res.results?.[0];
      toast(r?.ok ? `Test sent to ${user} ✓` : r?.error || "Test failed", r?.ok ? "success" : "error");
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setTesting(false);
    }
  };

  if (error) return <ApiError msg={error} onRetry={load} />;
  if (!s || !form) return <Spinner />;

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const fs = (k) => (e) => setForm((p) => ({ ...p, socials: { ...p.socials, [k]: e.target.value } }));

  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 items-start">
      <div className="flex flex-col gap-4 min-w-0">
        <Card className="p-5 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-slate-800">Wording</h3>
            <div className="flex gap-1 p-1 rounded-lg bg-slate-100">
              {[["initial", "Review email"], ["reminder", "Reminder"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setKind(k)}
                  className={`px-3 py-1 rounded-md text-xs font-semibold ${kind === k ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          {kind === "initial" ? (
            <>
              <Inp label="Subject" value={form.emailSubject} onChange={f("emailSubject")} />
              <Area label="Email" rows={18} value={form.emailBody} onChange={f("emailBody")} />
            </>
          ) : (
            <>
              <Inp label="Reminder subject" value={form.reminderSubject} onChange={f("reminderSubject")} />
              <Area label="Reminder email" rows={12} value={form.reminderBody} onChange={f("reminderBody")} />
            </>
          )}
          <div className="px-3.5 py-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-500 leading-relaxed">
            <p className="font-semibold text-slate-600 mb-1">How to write it</p>
            <p><code className="px-1 rounded bg-white">{"[Share Your Feedback]({link})"}</code> on its own line → the feedback button (required).</p>
            <p><code className="px-1 rounded bg-white">{"[Review Us on Instagram](https://…)"}</code> on its own line → an extra button. These also show on the thank-you page.</p>
            <p><code className="px-1 rounded bg-white">*text*</code> → <em>italic</em> · <code className="px-1 rounded bg-white">**text**</code> → <strong>bold</strong> · leave an empty line between paragraphs.</p>
            <p className="mt-1">Placeholders: <code>{"{firstName}"}</code> <code>{"{name}"}</code> (full name) <code>{"{destination}"}</code> <code>{"{serviceType}"}</code> <code>{"{dates}"}</code> <code>{"{brand}"}</code></p>
          </div>
          <div><Btn size="sm" variant="ghost" onClick={restoreWording}>Restore default wording</Btn></div>
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <h3 className="text-sm font-bold text-slate-800">Branding</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Brand name ({"{brand}"})</label>
              <div className="px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700">{s.brand?.name}</div>
              <p className={`text-[11px] ${s.brand?.fromEnv ? "text-slate-400" : "text-amber-600"}`}>
                {s.brand?.fromEnv
                  ? "Set on the server with BRAND_NAME."
                  : "BRAND_NAME isn't set on the server yet — add it to the backend environment."}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Button colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={/^#[0-9a-f]{6}$/i.test(form.brandColor) ? form.brandColor : "#111827"}
                  onChange={f("brandColor")} className="w-11 h-10 rounded-lg border border-slate-200 cursor-pointer" />
                <input value={form.brandColor} onChange={f("brandColor")} placeholder={s.brand?.color || BRAND_HEX}
                  className="flex-1 min-w-0 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm outline-none" />
              </div>
            </div>
          </div>
          <Inp label="Logo image address (https://…, optional)" placeholder={s.brand?.logoUrl || ""} value={form.logoUrl} onChange={f("logoUrl")} />
          {!form.logoUrl && s.brand?.logoUrl && <p className="text-[11px] text-slate-400 -mt-2">Using BRAND_LOGO_URL from the server. Type an address here to use a different one.</p>}
          <Inp label="Header picture address (https://…, optional)" value={form.heroImageUrl} onChange={f("heroImageUrl")} />
          <p className="text-[11px] text-slate-400 -mt-2">A wide photo shown under the logo, like an aeroplane or a destination shot. Use a link to an image that's already online.</p>
        </Card>

        <Card className="p-5 flex flex-col gap-4">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Social links</h3>
            <p className="text-xs text-slate-400 mt-0.5">Shown at the bottom of every review email. Leave blank to hide one.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {(s.socialKeys || []).map(({ key, label }) => (
              <Inp key={key} label={label} placeholder="https://…" value={form.socials?.[key] || ""} onChange={fs(key)} />
            ))}
          </div>
        </Card>

        <div className="sticky bottom-0 -mx-1 px-1 py-3 bg-slate-50/95 backdrop-blur flex flex-wrap items-center gap-2 border-t border-slate-200">
          <Btn onClick={save} loading={saving} disabled={!dirty}>Save emails</Btn>
          <Btn variant="secondary" onClick={sendTest} loading={testing}>Email me a test {kind === "reminder" ? "reminder" : ""}</Btn>
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
        </div>
      </div>

      {/* Live preview */}
      <div className="xl:sticky xl:top-2 min-w-0">
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Preview · sample client Ada Obi</p>
            <p className="text-sm font-semibold text-slate-800 mt-1 break-words">{preview?.subject || "…"}</p>
          </div>
          {preview?.html
            ? <iframe title="Email preview" srcDoc={preview.html} sandbox="allow-same-origin" className="w-full bg-slate-50"
                style={{ height: 820, border: 0 }}
                // Grow to fit the email (no scripts run inside: same-origin only lets us measure it).
                onLoad={(e) => { try { e.target.style.height = `${e.target.contentDocument.documentElement.scrollHeight + 8}px`; } catch { /* keep 820 */ } }} />
            : <Spinner />}
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// AUTOMATION
// ════════════════════════════════════════════════════════════════════════════
const SETTING_KEYS = ["autoCompleteTrips", "sendOnCompletion", "sendReminders", "reminderAfterDays", "publicUrl"];

function AutomationView() {
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth() || {};
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);

  const apply = (data) => {
    setS(data);
    const f = {};
    SETTING_KEYS.forEach((k) => { f[k] = data[k] ?? ""; });
    f.reminderAfterDays = data.reminderAfterDays || 7;
    setForm(f);
  };

  const load = useCallback(async () => {
    try { apply(await api.get("/crm/reviews/settings")); setError(null); }
    catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => {
    load();
    api.get("/crm/reviews/forms").then((r) => setForms(r.forms || [])).catch(() => {});
  }, [load]);

  const dirty = useMemo(() => s && form && SETTING_KEYS.some((k) => String(form[k] ?? "") !== String(s[k] ?? (k === "reminderAfterDays" ? 7 : ""))), [s, form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      SETTING_KEYS.forEach((k) => { payload[k] = form[k]; });
      payload.reminderAfterDays = Number(form.reminderAfterDays) || 7;
      payload.publicUrl = String(form.publicUrl || "").trim();
      apply(await api.patch("/crm/reviews/settings", payload));
      toast("Settings saved ✓");
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutomation = async () => {
    const on = !s.automationEnabled;
    if (dirty) return toast("Save your settings changes first", "warn");
    const ok = await confirm({
      title: on ? "Switch on automatic review emails?" : "Switch off automatic review emails?",
      message: on
        ? "From now on: trips are marked completed the day after they end, the review email goes out when a trip is completed, and a reminder follows if it isn't filled in. Trips that ended or were completed before now are left alone."
        : "No more automatic status changes, review emails or reminders. You can still send review emails yourself from a trip's Review tab.",
      confirmText: on ? "Switch on" : "Switch off",
      danger: !on,
    });
    if (!ok) return;
    try {
      apply(await api.patch("/crm/reviews/settings", { automationEnabled: on }));
      toast(on ? "Automation switched on ✓" : "Automation switched off");
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  if (error) return <ApiError msg={error} onRetry={load} />;
  if (!s || !form) return <Spinner />;

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const linkMissing = !s.effectivePublicUrl;

  return (
    <div className="flex flex-col gap-4">
      {/* Master switch */}
      <Card className="p-5 flex flex-wrap items-center gap-4">
        <div className={`w-3 h-3 rounded-full ${s.automationEnabled ? "bg-emerald-500" : "bg-slate-300"}`} />
        <div className="flex-1 min-w-[220px]">
          <p className="font-bold text-slate-800">Automatic review emails are {s.automationEnabled ? "on" : "off"}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {s.automationEnabled
              ? `Covering trips that ended or were completed since ${fmtDateTime(s.goLiveAt)}. Checked every 30 minutes.`
              : "Nothing is sent automatically. Test it below first, then switch it on."}
            {s.lastRunAt ? ` Last check ${fmtDateTime(s.lastRunAt)}.` : ""}
          </p>
        </div>
        <Btn variant={s.automationEnabled ? "danger" : "primary"} onClick={toggleAutomation} disabled={!s.automationEnabled && (linkMissing || !s.emailConnected)}>
          {s.automationEnabled ? "Switch off" : "Switch on"}
        </Btn>
      </Card>

      {(linkMissing || !s.emailConnected) && (
        <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-800 flex flex-col gap-1">
          <p className="font-semibold">Before switching on</p>
          {linkMissing && <p>• Add the CRM web address below — review links point there.</p>}
          {!s.emailConnected && <p>• The server can't send review emails yet: add <code>sendReviewEmail()</code> to <code>crm-email.service.ts</code> (see REVIEWS-SETUP.md).</p>}
        </div>
      )}

      <Card className="p-5 flex flex-col gap-4">
        <h3 className="text-sm font-bold text-slate-800">What runs automatically</h3>
        <Toggle checked={!!form.autoCompleteTrips} onChange={f("autoCompleteTrips")}
          label="Mark trips completed the day after they end"
          sub="Client trips and trip packages. Cancelled and paused trips are never changed." />
        <Toggle checked={!!form.sendOnCompletion} onChange={f("sendOnCompletion")}
          label="Email the review link when a trip is completed"
          sub="Whether staff, a trip package or the automation completes it. Sent once per trip." />
        <div className="flex flex-wrap items-center gap-3">
          <Toggle checked={!!form.sendReminders} onChange={f("sendReminders")} label="Send one reminder if it isn't filled in after" />
          <input type="number" min={1} max={60} value={form.reminderAfterDays} onChange={f("reminderAfterDays")} disabled={!form.sendReminders}
            className="w-20 px-3 py-1.5 rounded-lg border border-slate-200 text-sm outline-none disabled:opacity-50" />
          <span className="text-sm text-slate-600">days</span>
        </div>

        <div>
          <Inp label="CRM web address (review links point here)" placeholder={CRM_URL_EXAMPLE} value={form.publicUrl} onChange={f("publicUrl")} />
          <p className="text-xs text-slate-400 mt-1.5">
            Clients open <code className="px-1 rounded bg-slate-100">{(form.publicUrl || s.envPublicUrl || "https://…").replace(/\/+$/, "")}/?review=…</code>
            {origin && form.publicUrl !== origin && (
              <button type="button" onClick={() => setForm((p) => ({ ...p, publicUrl: origin }))} className="ml-2 font-semibold underline" style={{ color: BRAND_HEX }}>
                Use this address ({origin})
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Btn onClick={save} loading={saving} disabled={!dirty}>Save settings</Btn>
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
        </div>
      </Card>

      <TestSendCard forms={forms} defaultTo={user} ready={!linkMissing} />
      <DryRunCard enabled={s.automationEnabled} onRan={load} />
    </div>
  );
}

function Toggle({ checked, onChange, label, sub }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={onChange} className="mt-1" />
      <span>
        <span className="text-sm text-slate-700">{label}</span>
        {sub && <span className="block text-xs text-slate-400">{sub}</span>}
      </span>
    </label>
  );
}

function TestSendCard({ forms, defaultTo, ready }) {
  const toast = useToast();
  const [to, setTo] = useState(defaultTo || "");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { if (defaultTo) setTo((t) => t || defaultTo); }, [defaultTo]);
  useEffect(() => { if (forms.length && !picked) setPicked(forms.map((f) => f.tripType)); }, [forms, picked]);

  const send = async () => {
    if (!to.trim()) return toast("Enter an email address", "error");
    setBusy(true);
    setResults(null);
    try {
      const res = await api.post("/crm/reviews/test-send", { to: to.trim(), formTypes: picked });
      setResults(res.results || []);
      const okCount = (res.results || []).filter((r) => r.ok).length;
      toast(`${okCount} of ${(res.results || []).length} test emails sent`, okCount ? "success" : "error");
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-bold text-slate-800">Test every trip type</h3>
        <p className="text-xs text-slate-400 mt-0.5">
          Sends the review email for each trip type to you, marked [TEST], with sample trip details. Open them, fill them in,
          and check the answers under Responses → "Test reviews only". Clients are never emailed.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {forms.map((f) => (
          <label key={f.tripType} className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={!!picked?.includes(f.tripType)}
              onChange={(e) => setPicked((p) => (e.target.checked ? [...(p || []), f.tripType] : (p || []).filter((x) => x !== f.tripType)))} />
            {f.name || f.tripType}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]"><Inp label="Send to" value={to} onChange={(e) => setTo(e.target.value)} placeholder={STAFF_EMAIL_EXAMPLE} /></div>
        <Btn onClick={send} loading={busy} disabled={!ready || !picked?.length}>Send test emails</Btn>
      </div>
      {!ready && <p className="text-xs text-amber-600">Save the CRM web address first.</p>}
      {results && (
        <ul className="flex flex-col gap-1.5">
          {results.map((r, i) => (
            <li key={i} className="text-sm flex flex-wrap items-center gap-2">
              <span className={r.ok ? "text-emerald-600" : "text-rose-500"}>{r.ok ? "✓" : "✕"}</span>
              <span className="font-medium text-slate-700">{r.name || r.formType}</span>
              {r.ok ? (
                <a href={r.link} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: BRAND_HEX }}>open the form</a>
              ) : (
                <span className="text-xs text-rose-500">{r.error}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DryRunCard({ enabled, onRan }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(null);

  const preview = async () => {
    setBusy("preview");
    try { setReport(await api.get("/crm/reviews/automation/preview")); }
    catch (e) { toast(errMsg(e), "error"); }
    finally { setBusy(null); }
  };

  const runNow = async () => {
    const ok = await confirm({
      title: "Run the automation now?",
      message: "Trips that are due get completed and review emails / reminders that are due go out to clients now, instead of at the next scheduled check.",
      confirmText: "Run now",
      danger: false,
    });
    if (!ok) return;
    setBusy("run");
    try {
      const r = await api.post("/crm/reviews/automation/run", {});
      setReport(r);
      toast(r.ran ? "Done ✓" : r.reason || "Nothing ran", r.ran ? "success" : "warn");
      onRan?.();
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setBusy(null);
    }
  };

  const groups = report ? [
    ["Trip packages to mark completed", report.packagesCompleted, "Each linked client's trip is completed with it and gets a review email."],
    ["Client trips to mark completed", report.tripsCompleted, "Each gets a review email."],
    ["Review emails to send", report.reviewEmails, ""],
    ["Reminders to send", report.reminders, ""],
  ] : [];
  const nothing = report && groups.every(([, list]) => !list.length);

  return (
    <Card className="p-5 flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-bold text-slate-800">Check what happens next</h3>
        <p className="text-xs text-slate-400 mt-0.5">
          "Preview" lists what the next automatic check would do — nothing is changed or sent.
          {enabled ? "" : " While automation is off, it shows what would happen if you switched it on now."}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn variant="secondary" onClick={preview} loading={busy === "preview"}>Preview</Btn>
        {enabled && <Btn onClick={runNow} loading={busy === "run"}>Run now</Btn>}
      </div>
      {report && (
        <div className="flex flex-col gap-4">
          <p className="text-xs text-slate-400">
            {report.dryRun ? "Preview" : "Ran"} at {fmtDateTime(report.at)}{nothing ? " — nothing is due." : ""}
          </p>
          {groups.map(([title, list, note]) => list.length > 0 && (
            <div key={title}>
              <p className="text-sm font-semibold text-slate-700">{title} ({list.length})</p>
              {note && <p className="text-xs text-slate-400 mb-1.5">{note}</p>}
              <ul className="flex flex-col gap-1">
                {list.map((it, i) => (
                  <li key={i} className="text-xs text-slate-600 flex flex-wrap gap-x-2">
                    <span className="font-medium text-slate-700">{it.name}</span>
                    {it.destination && <span>· {it.destination}</span>}
                    {it.tripType && <span>· {it.tripType}</span>}
                    {it.email && <span className="text-slate-400">· {it.email}</span>}
                    {it.date && <span className="text-slate-400">· {fmtDay(it.date)}</span>}
                    {it.result && <span className={/fail|error/.test(it.result) ? "text-rose-500" : "text-emerald-600"}>— {it.result}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
